import * as React from 'react'

import { cva, type VariantProps } from 'cva'

import { Eye, EyeSlash, MagnifyingGlassMini } from '@/components/icons'

import { cn } from '@/libs/cn'

const inputBaseStyles = cn(
	'relative w-full appearance-none rounded-md bg-ui-bg-field text-ui-fg-base placeholder-ui-tag-neutral-text caret-ui-fg-base shadow-borders-base outline-none transition-fg hover:bg-ui-bg-field-hover',
	'focus-visible:shadow-borders-interactive-with-active',
	'disabled:cursor-not-allowed disabled:!bg-ui-bg-disabled disabled:text-ui-fg-subtle disabled:placeholder-ui-fg-disabled',
	'invalid:!shadow-borders-error aria-[invalid=true]:!shadow-borders-error'
)

const inputVariants = cva({
	base: cn(inputBaseStyles),
	variants: {
		size: {
			base: 'txt-compact-small h-8 px-2 py-1.5',
			small: 'txt-compact-small h-7 px-2 py-1',
		},
	},
	defaultVariants: {
		size: 'base',
	},
})

interface InputProps
	extends VariantProps<typeof inputVariants>,
		Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, type, size = 'base', ...props }: InputProps, ref) => {
		const [typeState, setTypeState] = React.useState(type)

		const isPassword = type === 'password'
		const isSearch = type === 'search'

		return (
			<div className="relative">
				<input
					ref={ref}
					type={isPassword ? typeState : type}
					className={cn(
						inputVariants({ size: size }),
						{
							'pl-8': isSearch && size === 'base',
							'pr-8': isPassword && size === 'base',
							'pl-7': isSearch && size === 'small',
							'pr-7': isPassword && size === 'small',
						},
						className
					)}
					{...props}
				/>
				{isSearch && (
					<div
						className={cn(
							'pointer-events-none absolute bottom-0 left-0 flex items-center justify-center text-ui-fg-muted',
							{
								'h-8 w-8': size === 'base',
								'h-7 w-7': size === 'small',
							}
						)}
						role="img"
					>
						<MagnifyingGlassMini />
					</div>
				)}
				{isPassword && (
					<div
						className={cn(
							'absolute bottom-0 right-0 flex items-center justify-center border-l',
							{
								'h-8 w-8': size === 'base',
								'h-7 w-7': size === 'small',
							}
						)}
					>
						<button
							className="focus-visible:shadow-borders-interactive-w-focus h-fit w-fit rounded-sm text-ui-fg-muted outline-none transition-all hover:text-ui-fg-base focus-visible:text-ui-fg-base active:text-ui-fg-base"
							type="button"
							onClick={() => {
								setTypeState(typeState === 'password' ? 'text' : 'password')
							}}
						>
							<span className="sr-only">
								{typeState === 'password' ? 'Show password' : 'Hide password'}
							</span>
							{typeState === 'password' ? <Eye /> : <EyeSlash />}
						</button>
					</div>
				)}
			</div>
		)
	}
)
Input.displayName = 'Input'

export { Input, inputBaseStyles }
